<template>
  <div
    :class="[
      'tw:group tw:h-10 tw:p-2 tw:flex tw:justify-between tw:hover:bg-denim-100',
      { 'tw:cursor-pointer': urlStatus !== UrlStatus.cannotBeAdded },
    ]"
    @click="performRowAction"
  >
    <div class="tw:flex tw:overflow-hidden">
      <div class="tw:mr-3 tw:w-[19px] tw:content-center">
        <QSpinner
          v-if="urlStatus === UrlLocalStatus.pending"
          data-testid="verification-pending-spinner"
          :size="19"
        />
        <BaseIcon
          v-else-if="statusIcon"
          data-testid="verification-status"
          :icon="statusIcon"
          :icon-class="`${statusColorClass} tw:text-[1.65em]!`"
        />
      </div>
      <div
        class="tw:content-center tw:truncate"
        :class="urlTextColorClass"
      >
        <span
          class="tw:align-middle"
          data-testid="url-value"
        >
          {{ urlData.url }}
        </span>
        <BaseStreemTooltip>{{ urlData.url }}</BaseStreemTooltip>
      </div>
    </div>
    <span
      v-if="urlStatus === UrlStatus.cannotBeAdded"
      class="tw:content-center tw:ml-4"
      :class="statusColorClass"
      data-testid="cannot-be-added-reason"
    >
      {{ cannotBeAddedReasonText }}
    </span>
    <div
      v-else-if="urlStatus === UrlStatus.alreadyAdded"
      class="tw:content-center tw:-my-2 tw:hidden tw:group-hover:block"
    >
      <BaseButton
        button-class="tw:bg-transparent! tw:border-none! tw:hover:bg-denim-200!"
        circle
        data-testid="preview-button"
        icon="full-article"
        icon-size="md"
        new-design
        new-icon
      />
      <BaseStreemTooltip>{{
        getLocaleText("global.preview")
      }}</BaseStreemTooltip>
    </div>
    <BaseButton
      v-else-if="urlStatus === UrlStatus.canBeAdded"
      class="tw:bg-blue-700! tw:hover:bg-blue-600! tw:h-6! tw:min-h-6! tw:text-white! tw:border-none!"
      data-testid="add-url-button"
      :disabled="disableButtons"
      :label="getLocaleText('global.add')"
      new-design
    />
  </div>
</template>

<script setup lang="ts">
import { type DesignSystemIcons } from "design-system/types";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseIcon,
  BaseStreemTooltip,
} from "shared/components/base";
import { openLink } from "shared/helpers/external";
import { compoundId } from "shared/helpers/mentions";
import { type Maybe, type Nullable } from "shared/types";
import { MentionType } from "shared/types/mentions";

import { urlAlreadyAdded, urlCanBeAdded } from "../shared/helpers";
import { type UrlData, UrlLocalStatus } from "../shared/types";
import { UrlStatus } from "../useAddExternalItemsWorkflow/useUrlVerificationNotificationsSubscription";

import { URL_STATUSES_UI_CONFIG } from "./shared";

defineOptions({
  name: "UrlDataRow",
});

export type UrlDataRowProps = {
  disableButtons?: boolean;
  urlData: UrlData;
};

const props = defineProps<UrlDataRowProps>();

const emit = defineEmits(["addUrl"]);

const urlStatus = computed<UrlData["status"]>(() => props.urlData.status);

const statusIcon = computed<DesignSystemIcons | undefined>(() => {
  if (urlStatus.value === UrlLocalStatus.pending) return undefined;

  return {
    [UrlStatus.alreadyAdded]: "check",
    [UrlStatus.canBeAdded]: "plus-circle",
    [UrlStatus.cannotBeAdded]: "exclamation-circle",
  }[urlStatus.value] as DesignSystemIcons;
});

const statusColorClass = computed<Maybe<string>>(() => {
  if (urlStatus.value === UrlLocalStatus.pending) return undefined;

  return URL_STATUSES_UI_CONFIG.get(urlStatus.value);
});

const urlTextColorClass = computed<string | undefined>(() =>
  urlStatus.value === UrlStatus.alreadyAdded ? "" : statusColorClass.value
);

const cannotBeAddedReasonText = computed<string>(() =>
  getLocaleText(
    `external_items_bulk_add_modal.cannot_be_added_details.${props.urlData.reason}`
  )
);

const mentionPreviewUrl = computed<Nullable<string>>(() => {
  if (!urlAlreadyAdded(props.urlData)) return null;

  if (props.urlData.mentionType === MentionType.external_item) {
    return props.urlData.url;
  }

  // for alreadyAdded URLs mentionId is always present, except for "addedInCurrentFlow" external items
  // which are being handled above along with the actually existing external items
  const mentionCompoundId = compoundId({
    type: props.urlData.mentionType,
    id: props.urlData.mentionId!,
  });

  return `${import.meta.env.VITE_DESKTOP_APP_URL}/view/${mentionCompoundId}`;
});

function performRowAction() {
  if (urlAlreadyAdded(props.urlData)) {
    openLink(mentionPreviewUrl.value!);
  } else if (urlCanBeAdded(props.urlData)) {
    emit("addUrl");
  }
}
</script>
